import useAuthContext from '@salesforce/commerce-sdk-react/hooks/useAuthContext'
import {getMrtApiBasepath} from '../ssr/helpers'
import {useAccessToken} from '@salesforce/commerce-sdk-react'
import useMultiSite from '@salesforce/retail-react-app/app/hooks/use-multi-site'

export async function searchCustomerByEmail(email, token, customerId) {
    const endpoint = origin + getMrtApiBasepath('/search-customers?email=' + email)

    const options = {
        headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
            customerid: customerId,
        },
        method: 'POST',
        credentials: 'same-origin',
    }

    const response = await fetch(`${endpoint}`, options)
    return await response.json()
}
/**
 * Custom hook to handle Gigya SSR logic.
 * @returns {Object} - State and utility functions for Gigya SSR.
 */

/**
 * Custom hook for interacting with Gigya-related server-side APIs.
 * Provides methods to get account information, retrieve a lite token, set account information,
 * and resend DOI (Double Opt-In) confirmation emails.
 *
 * @returns {Object} An object containing the following methods:
 *
 * @method
 * async getAccountInfo(uid, regToken) - Fetches account information based on the provided UID or registration token.
 * @param {string} [uid] - The unique identifier of the user.
 * @param {string} [regToken] - The registration token of the user.
 * @returns {Promise<Object>} A promise resolving to the account information.
 *
 * @method
 * async getLiteToken(email) - Retrieves a lite token for the given email address.
 * @param {string} email - The email address of the user.
 * @returns {Promise<Object>} A promise resolving to the lite token information.
 *
 * @method
 * async setAccountInfo(params) - Updates account information with the provided parameters.
 * @param {Object} params - The parameters to update the account information.
 * @returns {Promise<Object>} A promise resolving to the response of the update operation.
 *
 * @method
 * async resendDOIConfirmationEmail(params) - Resends the DOI confirmation email with the provided parameters.
 * @param {Object} params - The parameters required to resend the DOI confirmation email.
 * @returns {Promise<Object>} A promise resolving to the response of the resend operation.
 */

const useGigyaSSR = () => {
    const {getTokenWhenReady} = useAccessToken()
    const auth = useAuthContext()
    const {site} = useMultiSite()

    const getCustomerId = () => {
        const onClient = typeof window !== 'undefined'
        if (onClient) {
            return localStorage.getItem(`customer_id_${site.id}`)
        }
        return auth.get('customer_id')
    }

    async function getAccountInfo(uid, regToken) {
        const token = await getTokenWhenReady()
        const customerId = getCustomerId()

        const query = uid ? '?uid=' + uid : regToken ? '?regToken=' + regToken : ''
        const endpoint = origin + getMrtApiBasepath('/get-gigya-user-info' + query)

        const options = {
            headers: {
                'Content-Type': 'application/json',
                authorization: `Bearer ${token}`,
                customerid: customerId,
            },
            method: 'GET',
            credentials: 'same-origin',
        }

        const response = await fetch(`${endpoint}`, options)
        return await response.json()
    }

    async function getLiteToken(email) {
        const token = await getTokenWhenReady()
        const customerId = getCustomerId()

        const endpoint = origin + getMrtApiBasepath('/get-gigya-lite-token?email=' + email)

        const options = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                customerid: customerId,
            },
            method: 'GET',
            credentials: 'same-origin',
        }

        const response = await fetch(`${endpoint}`, options)
        return await response.json()
    }

    async function setAccountInfo(params) {
        const token = await getTokenWhenReady()
        const customerId = getCustomerId()

        const endpoint = origin + getMrtApiBasepath('/set-gigya-user-info')

        const options = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                customerid: customerId,
            },
            method: 'POST',
            credentials: 'same-origin',
            body: JSON.stringify(params),
        }

        const response = await fetch(`${endpoint}`, options)

        return await response.json()
    }

    async function resendDOIConfirmationEmail(params) {
        const token = await getTokenWhenReady()
        const customerId = getCustomerId()

        const endpoint = origin + getMrtApiBasepath('/resend-doi-confirmation-email')

        const options = {
            headers: {
                'Content-Type': 'application/json',
                Authorization: `Bearer ${token}`,
                customerid: customerId,
            },
            method: 'POST',
            credentials: 'same-origin',
            body: JSON.stringify(params),
        }

        const response = await fetch(`${endpoint}`, options)

        return await response.json()
    }
    return {setAccountInfo, getAccountInfo, getLiteToken, resendDOIConfirmationEmail}
}

export default useGigyaSSR
